import { Switch, FormControlLabel, Typography, Box, Tooltip, IconButton } from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useSelector, useDispatch } from 'react-redux';
import { useCallback } from 'react';
import { selectIsBrzFinanceEnabled, setBrzFinanceEnabled } from '@state/app-context-slice';
import { logger } from '@utils/logging-utils';
import { isBrzFinanceSwitchEditable, isProductionEnvironment } from '@utils/environment-utils';
import InfoOutlined from '@mui-symbols-material/w400/InfoOutlined';

/**
 * Props for the BrzFinanceToggle component.
 *
 * @interface BrzFinanceToggleProps
 */
interface BrzFinanceToggleProps {
  /** Optional CSS class name */
  readonly className?: string;
  /** Optional data-testid for testing */
  readonly 'data-testid'?: string;
  /** Show debug information */
  readonly debug?: boolean;
}

const LOG_CONTEXT = 'BrzFinanceToggle';

/**
 * Debug component to show current BRZ Finance state values
 */
const BrzFinanceDebugInfo = ({ isBrzFinanceEnabled }: { isBrzFinanceEnabled: boolean }) => {
  const isEditable = isBrzFinanceSwitchEditable();
  const isProduction = isProductionEnvironment();

  return (
    <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
      <Typography variant="caption" component="div">
        <strong>Debug Info:</strong>
      </Typography>
      <Typography variant="caption" component="div">
        Redux State: {isBrzFinanceEnabled ? 'true' : 'false'}
      </Typography>
      <Typography variant="caption" component="div">
        Environment: {isProduction ? 'production' : 'non-production'}
      </Typography>
      <Typography variant="caption" component="div">
        Editable: {isEditable ? 'true' : 'false'}
      </Typography>
    </Box>
  );
};

/**
 * Toggle component for enabling/disabling BRZ Finance (Sage) integration.
 * Manages the state via Redux. The BRZ Finance setting will be provided by the backend.
 *
 * When enabled, this affects:
 * - Contact number generation behavior in business partners
 * - Field readonly states in various forms
 * - Integration with BRZ Finance backend systems
 *
 * Environment-based behavior:
 * - Testing/Staging: Switch is editable and can be toggled
 * - Production: Switch is read-only, shows current state but cannot be changed
 *
 * @param {Readonly<BrzFinanceToggleProps>} props - Component props
 * @returns {React.ReactNode} The rendered toggle component
 *
 * @category Components/Common/User
 * @component
 */
export function BrzFinanceToggle({
  className,
  'data-testid': dataTestId = 'brz-finance-toggle',
  debug = false
}: Readonly<BrzFinanceToggleProps>): React.ReactNode {
  const { t } = useTranslation('settings');
  const dispatch = useDispatch();

  // Get current state from Redux
  const isBrzFinanceEnabled = useSelector(selectIsBrzFinanceEnabled);

  // Check if the switch should be editable based on environment
  const isEditable = isBrzFinanceSwitchEditable();
  const isProduction = isProductionEnvironment();

  /**
   * Handle toggle change - updates Redux state only if environment allows editing
   */
  const handleToggleChange = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      // Prevent changes in production environment
      if (!isEditable) {
        logger.warn(LOG_CONTEXT, 'Attempted to change BRZ Finance toggle in production environment', {
          environment: 'production',
          attemptedValue: event.target.checked,
          currentValue: isBrzFinanceEnabled
        });
        return;
      }

      const newValue = event.target.checked;

      logger.info(LOG_CONTEXT, 'Toggle changed', {
        newValue,
        previousValue: isBrzFinanceEnabled,
        environment: isProduction ? 'production' : 'non-production'
      });

      // Update Redux state
      dispatch(setBrzFinanceEnabled(newValue));
    },
    [dispatch, isBrzFinanceEnabled, isEditable, isProduction]
  );

  return (
    <Box className={className} data-testid={dataTestId}>
      <FormControlLabel
        control={
          <Switch
            checked={isBrzFinanceEnabled}
            onChange={handleToggleChange}
            name="brzFinanceEnabled"
            color="primary"
            data-testid={`${dataTestId}-switch`}
            disabled={!isEditable}
          />
        }
        label={
          <Box>
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
              <Typography variant="body1" component="span">
                {t('brzFinanceToggle.label')}
              </Typography>
              <Tooltip title={isEditable ? t('brzFinanceToggle.contactNumberTooltip') : t('brzFinanceToggle.productionReadOnlyTooltip')} arrow>
                <IconButton size="small" sx={{ padding: 0.5 }} data-testid={`${dataTestId}-contact-info-button`}>
                  <InfoOutlined fontSize="small" color="action" />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="body2" color="text.secondary" component="div" sx={{ mt: 0.5 }}>
              {isEditable ? t('brzFinanceToggle.description') : t('brzFinanceToggle.productionDescription')}
            </Typography>
            <Typography
              variant="caption"
              color={isBrzFinanceEnabled ? 'success.main' : 'text.disabled'}
              component="div"
              sx={{ mt: 0.5, fontWeight: 'medium' }}>
              {isBrzFinanceEnabled ? t('brzFinanceToggle.enabled') : t('brzFinanceToggle.disabled')}
              {!isEditable && (
                <Typography variant="caption" color="warning.main" component="span" sx={{ ml: 1 }}>
                  ({t('brzFinanceToggle.readOnly')})
                </Typography>
              )}
            </Typography>
          </Box>
        }
        data-testid={`${dataTestId}-label`}
      />

      {debug && <BrzFinanceDebugInfo isBrzFinanceEnabled={isBrzFinanceEnabled} />}
    </Box>
  );
}
